import { BreakpointObserver } from "@angular/cdk/layout";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { Entity } from "@app/entity/model/entity";
import { EntityService } from "@app/entity/services/entity.service";
import { RouterActions } from "@app/root-store";
import { MenuUtility } from "@app/shared/services/utils/menu-utility.service";
import { Store } from "@ngrx/store";
import { NgxPermissionsObject, NgxPermissionsService } from "ngx-permissions";
import { Observable, Subject, combineLatest, distinctUntilChanged, filter, map, takeUntil } from "rxjs";
type GenericMethod<T> = () => T;

interface InfoBannerItem {
    message: string;
    method: GenericMethod<void>;
    buttonText: string;
}

@Component({
    selector: "app-info-banner",
    templateUrl: "./info-banner.component.html",
    styleUrls: ["./info-banner.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InfoBannerComponent implements OnInit, OnDestroy {
    public bannerItems: InfoBannerItem[] = [];
    public isSideNavOpen = false;
    public hasSecondaryNav = false;
    public isMobile = false;

    private unsubscribe$ = new Subject();
    private entity$: Observable<Entity>;
    private isMobile$: Observable<boolean>;
    private isMenuOpen$: Observable<{ isOpen: boolean; hasSecondaryNav: boolean }>;

    constructor(
        private readonly entityService: EntityService,
        private readonly cdr: ChangeDetectorRef,
        private readonly store: Store,
        private readonly permissionsService: NgxPermissionsService,
        private readonly menuService: MenuUtility,
        private readonly breakpointObserver$: BreakpointObserver
    ) {}

    ngOnInit(): void {
        this.isMobile$ = this.breakpointObserver$.observe(["(max-width: 959px)"]).pipe(
            takeUntil(this.unsubscribe$),
            map(mobileQuery => mobileQuery.matches),
            distinctUntilChanged()
        );

        this.isMenuOpen$ = this.menuService.menuOpen$.pipe(takeUntil(this.unsubscribe$));

        combineLatest([this.isMobile$, this.isMenuOpen$])
            .pipe(
                takeUntil(this.unsubscribe$),
                map(([isMobile, menuOpenStatus]) => {
                    this.isMobile = isMobile;
                    this.isSideNavOpen = menuOpenStatus.isOpen;
                    this.hasSecondaryNav = menuOpenStatus.hasSecondaryNav;
                    this.cdr.detectChanges();
                })
            )
            .subscribe();

        this.entityService.getEntityList().pipe(takeUntil(this.unsubscribe$)).subscribe();

        this.entity$ = this.entityService.getAuthUserEntity().pipe(
            takeUntil(this.unsubscribe$),
            filter(entity => !!entity)
        );

        combineLatest([this.permissionsService.permissions$, this.entity$])
            .pipe(
                takeUntil(this.unsubscribe$),
                map(([permissions, entity]) => {
                    this.buildBannerInfoArray(permissions, entity);
                })
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    private buildBannerInfoArray(permissions: NgxPermissionsObject, entity: Entity): void {
        this.bannerItems = [];
        // Entity Support Contact Info
        if (!entity.supportEmail && !entity.supportPhone) {
            if (this.hasPermission(permissions, ["viewCompany", "editCompany", "editCompanySettings"])) {
                this.bannerItems.push({
                    message: "We are adding the ability to share your company contact information with ClareOne customers.",
                    method: () => this.goToEditCompanyInfo(entity.id),
                    buttonText: "Update Now",
                });
            } else {
                this.bannerItems.push({
                    message: `We are adding the ability to share your contact information with ClareOne customers.
                         Please reach out to your company admin to update this info.`,
                    method: null,
                    buttonText: null,
                });
            }
        }

        this.cdr.detectChanges();
    }

    private goToEditCompanyInfo(entityId: number): void {
        this.store.dispatch(
            RouterActions.routerGo({
                path: [`entities/edit/${entityId}/companyinfo`],
            })
        );
    }

    private hasPermission(userPermissions: NgxPermissionsObject, permissionsRequired: string[]): boolean {
        return Object.keys(userPermissions).some(key => permissionsRequired.includes(key));
    }
}
